/**
 * DayNumber — a dense day cell's date, with the TODAY marker (BON-146).
 *
 * Today is derived here from the real clock rather than threaded down as a prop
 * from the picker. It is the same fact for every call-site and there is no
 * caller who should be able to disagree about which day it is, so a prop would
 * only create a way for the calendar to be wrong.
 *
 * Deliberately separate from the "Current" event badge: Current marks the
 * workshop a booking is moving OFF, Today anchors the CSR in the month, and the
 * two land on the same cell only by coincidence. Conflating them would make a
 * customer whose workshop happens to be today read as "the current one is
 * today" — true, but for two unrelated reasons.
 */
import { format, isToday } from 'date-fns';

export interface DayNumberProps {
  day: Date;
  /** Days spilling in from the adjacent month render muted. */
  inMonth: boolean;
}

export function DayNumber({ day, inMonth }: DayNumberProps) {
  const today = isToday(day);
  if (!today) {
    return (
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          color: inMonth ? 'var(--nb-ink)' : 'var(--nb-faint)',
        }}
      >
        {format(day, 'd')}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        aria-label={`Today, ${format(day, 'MMMM d')}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 17,
          height: 17,
          padding: '0 4px',
          borderRadius: 999,
          fontSize: 10.5,
          fontWeight: 800,
          background: 'var(--nb-primary, #6b8e5a)',
          color: '#fff',
        }}
      >
        {format(day, 'd')}
      </span>
      <span
        style={{
          fontSize: 8.5,
          fontWeight: 800,
          letterSpacing: '.05em',
          textTransform: 'uppercase',
          color: 'var(--nb-primary, #6b8e5a)',
        }}
      >
        Today
      </span>
    </span>
  );
}
